import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Center,
  Combobox,
  Group,
  Loader,
  Pagination,
  Stack,
  Table,
  Text,
  Tooltip,
  useCombobox,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDisclosure, useHotkeys, useSessionStorage } from '@mantine/hooks';
import {
  IconChevronDown,
  IconExclamationCircle,
  IconFilter,
  IconFilterOff,
  IconMailForward,
  IconProgressCheck,
  IconSortAscending,
  IconSortDescending,
  IconZoom,
} from '@tabler/icons-react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { ClientHoverCard } from 'components/ClientHoverCard';
import { PageTitle } from 'components/PageTitle';
import dayjs from 'dayjs';
import { useProfile } from 'init/use-enforce-profile';
import { i18nKeys, type TranslateFunction, useTranslation } from 'locales/index';
import { countActiveFilters } from 'modules/Kanban/utils/filters';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { isNonNullish } from 'remeda';
import { handleRowAuxClick, handleRowClick } from 'shared/utils/table';
import { type LightReminder, type LoadRemindersVariables, useLoadReminders } from '../api';
import { FilterDrawer, SentMediumBadge } from '../components';

const STORAGE_KEY = 'sent-reminders-filters';

const columnHelper = createColumnHelper<LightReminder>();

const columns = (order: 'asc' | 'desc', onToggleOrder: VoidFunction, t: TranslateFunction) => [
  columnHelper.accessor('reference', {
    header: t(i18nKeys.REMINDER_LIST.HEADERS.REFERENCE),
    cell: reference => {
      const row = reference.row.original;

      const shouldShowBadge = row.invoiceCount > 1;
      return (
        <Group>
          {reference.getValue()}
          {shouldShowBadge && <Badge variant="light">{row.invoiceCount}</Badge>}
          {row.failed && (
            <Tooltip label={t(i18nKeys.REMINDER_LIST.FAILED_REMINDER_LABEL)}>
              <IconExclamationCircle color="orange" />
            </Tooltip>
          )}
          {row.automaticResent && (
            <Tooltip label={t(i18nKeys.REMINDER_LIST.AUTOMATICALLY_RESENT_BY_POST)}>
              <IconMailForward color="green" />
            </Tooltip>
          )}
        </Group>
      );
    },
  }),
  columnHelper.accessor('sentAt', {
    header: () => (
      <Group onClick={onToggleOrder} style={{ cursor: 'pointer', userSelect: 'none' }}>
        {t(i18nKeys.REMINDER_LIST.HEADERS.SENT_AT)}
        <ActionIcon variant="transparent" color="purple.5">
          {order === 'asc' ? <IconSortAscending size={14} /> : <IconSortDescending size={14} />}
        </ActionIcon>
      </Group>
    ),
    cell: sentAt => dayjs(sentAt.getValue()).format('L'),
  }),
  columnHelper.accessor('debtorFullName', {
    header: t(i18nKeys.REMINDER_LIST.HEADERS.DEBTOR),
    cell: info => {
      const { debtorId, debtorFullName } = info.row.original;

      return <ClientHoverCard debtorId={debtorId} debtorName={debtorFullName} />;
    },
  }),
  columnHelper.accessor('reminderType', {
    header: t(i18nKeys.REMINDER_LIST.HEADERS.REMINDER_TYPE),
  }),
  columnHelper.accessor('sentMedia', {
    header: t(i18nKeys.REMINDER_LIST.HEADERS.SENT_MEDIA),
    cell: sentMedia => (
      <Group gap={4}>
        {sentMedia.getValue().map(sentMedium => (
          <SentMediumBadge key={sentMedium} sentMedium={sentMedium} />
        ))}
      </Group>
    ),
  }),
];

export type RemindersFilter = Omit<LoadRemindersVariables, 'page' | 'pageLimit'>;

export const ReminderList = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const profile = useProfile();

  const [currentPage, setCurrentPage] = useState(1);
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [isDrawerOpen, { open: openDrawer, close: closeDrawer, toggle: toggleDrawerOpen }] =
    useDisclosure(false);
  useHotkeys([['f', toggleDrawerOpen]]);

  const filterForm = useForm<RemindersFilter>({
    onValuesChange: values => {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(values));
    },
  });

  useEffect(() => {
    const storedValue = window.sessionStorage.getItem(STORAGE_KEY);
    if (isNonNullish(storedValue)) {
      filterForm.setValues(JSON.parse(storedValue));
    }
  }, []);

  const [pageLimit, setPageLimit] = useSessionStorage({
    key: 'remindersIndexPageLimit',
    defaultValue: profile.preferences.itemsPerPage,
  });

  const { reminders, isRemindersFetching } = useLoadReminders({
    page: currentPage,
    pageLimit,
    ...filterForm.values,
    order,
  });

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const handleToggleOrder = () => {
    setOrder(currentOrder => (currentOrder === 'asc' ? 'desc' : 'asc'));
  };

  const table = useReactTable({
    data: reminders.data,
    columns: columns(order, handleToggleOrder, t),
    getCoreRowModel: getCoreRowModel(),
  });

  const activeFiltersCount = countActiveFilters(filterForm.values);
  const noRemindersFound = reminders?.data.length === 0 && !isRemindersFetching;
  const hasNoReminder = noRemindersFound && activeFiltersCount === 0;
  const noFilteredReminders = noRemindersFound && activeFiltersCount > 0;

  const options = [10, 20, 50, 100].map(item => (
    <Combobox.Option value={item.toString()} key={item}>
      {item}
    </Combobox.Option>
  ));

  const firstItem = (currentPage - 1) * reminders.pagination.pageLimit + 1;
  const lastItem = (currentPage - 1) * reminders.pagination.pageLimit + reminders.data.length;

  return (
    <>
      <Stack h="100%" gap={0}>
        <PageTitle>
          {t(i18nKeys.REMINDER_LIST.TITLE)}
          <PageTitle.Actions>
            <Button
              variant="default"
              leftSection={<IconFilter size={18} stroke={1.5} />}
              rightSection={
                <Badge color={activeFiltersCount > 0 ? 'purple' : 'gray'}>
                  {activeFiltersCount}
                </Badge>
              }
              onClick={openDrawer}
            >
              {t(i18nKeys.REMINDER_LIST.FILTERS.TITLE)}
            </Button>
            {activeFiltersCount > 0 && (
              <Button variant="default" onClick={filterForm.reset}>
                <IconFilterOff stroke={1.5} size={18} />
              </Button>
            )}
          </PageTitle.Actions>
        </PageTitle>
        <Card radius={8} p={0}>
          <Group style={{ overflowY: 'auto' }}>
            <Table
              highlightOnHover
              highlightOnHoverColor="purple.0"
              horizontalSpacing="md"
              verticalSpacing="space3"
              stickyHeader
              fz={12}
            >
              <Table.Thead bg="purple.1" style={{ fontSize: 14 }}>
                {table.getHeaderGroups().map(headerGroup => (
                  <Table.Tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <Table.Th key={header.id} c="purple.5" fw={400}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </Table.Th>
                    ))}
                  </Table.Tr>
                ))}
              </Table.Thead>
              <Table.Tbody>
                {table.getRowModel().rows.map(row => (
                  <Table.Tr
                    key={row.id}
                    onClick={event =>
                      handleRowClick(event, `/reminders/${row.original.id}`, history.push)
                    }
                    onAuxClick={event => handleRowAuxClick(event, `/reminders/${row.original.id}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    {row.getVisibleCells().map(cell => (
                      <Table.Td key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </Table.Td>
                    ))}
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            {hasNoReminder && (
              <Center mb="md" w="100%">
                <Stack align="center">
                  <IconProgressCheck size={75} color="gray" />
                  <Text size="sm">{t(i18nKeys.REMINDER_LIST.EMPTY_INDEX)}</Text>
                </Stack>
              </Center>
            )}
            {noFilteredReminders && (
              <Center mb="md" w="100%">
                <Stack align="center">
                  <IconZoom size={75} color="gray" />
                  <Text size="xl" fw={700}>
                    {t(i18nKeys.REMINDER_LIST.FILTERS.NOT_FOUND_TITLE)}
                  </Text>
                  <Text size="sm" c="grey">
                    {t(i18nKeys.REMINDER_LIST.FILTERS.NOT_FOUND_DESCRIPTION)}
                  </Text>
                </Stack>
              </Center>
            )}
            {isRemindersFetching && (
              <Center mb="md" w="100%">
                <Loader />
              </Center>
            )}
          </Group>
          <Group
            justify="space-between"
            p="sm"
            style={theme => ({
              borderTop: `1px solid ${theme.colors.gray[2]}`,
            })}
          >
            <Group>
              <Text size="sm">
                {firstItem} - {lastItem} {t(i18nKeys.OF)} {reminders.pagination.totalObjects}
              </Text>
              <Combobox
                store={combobox}
                width={250}
                position="bottom-start"
                withArrow
                onOptionSubmit={val => {
                  setPageLimit(Number(val));
                  combobox.closeDropdown();
                }}
              >
                <Combobox.Target>
                  <Group
                    onClick={() => combobox.toggleDropdown()}
                    style={{ cursor: 'pointer' }}
                    gap={4}
                  >
                    <Text size="sm">
                      {pageLimit} {t(i18nKeys.PER_PAGE)}
                    </Text>
                    <IconChevronDown stroke={1.5} size={16} />
                  </Group>
                </Combobox.Target>

                <Combobox.Dropdown>
                  <Combobox.Options>{options}</Combobox.Options>
                </Combobox.Dropdown>
              </Combobox>
            </Group>
            <Pagination
              value={currentPage}
              onChange={page => setCurrentPage(page)}
              total={reminders.pagination.totalPages}
              size="xs"
            />
          </Group>
        </Card>
      </Stack>
      <FilterDrawer isOpen={isDrawerOpen} close={closeDrawer} form={filterForm} />
    </>
  );
};
